import React, {Component} from "react";
import PropTypes from "prop-types";
import {debounce, isFinite, isNil, maxBy} from "lodash";
import {getAggregatedData} from "../../actions/aggregatedActions";
import NewIOT from "../../IOT/NewIOT";
import moment from "moment";
import AgoTime from "../basics/ago-time/AgoTime";
import Button from "../basics/button/Button";
import {localDateTimeFormatter} from "../../utils/DateTimeUtils";
import "./_fetcher.scss";

const DEBUG = true;

class Fetcher extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clicked: false,
        };
        this.requestInterval = null;
    }

    componentDidMount() {
        const {device, index, currentDayRefreshTime} = this.props;
        // zapytanie o dane na aggData
        this.fetchData();
        DEBUG &&
            console.log(
                "[AggFetcher] componentDidMount",
                device,
                index,
                currentDayRefreshTime
            );
        this.setupInterval();
    }

    setupInterval = () => {
        const {currentDayRefreshTime} = this.props;
        clearInterval(this.requestInterval);
        if (currentDayRefreshTime) {
            DEBUG && console.log("[AggFetcher] starting interval");
            this.requestInterval = setInterval(
                this.requestMQTTAggData,
                currentDayRefreshTime
            );
        }
    };

    requestMQTTAggData = () => {
        DEBUG && console.log("[AggFetcher] requestMQTTAggData called");
        const {device} = this.props;
        if (device && device.GatewayID) {
            DEBUG && console.log("[AggFetcher] sendRequestForAggregatedData");
            NewIOT.sendRequestForAggregatedData(device);
        }
    };

    componentWillUnmount() {
        DEBUG && console.log("[AggFetcher] clearing interval");
        clearInterval(this.requestInterval);
        clearTimeout(this.timeout);
        this.fetchData.cancel();
    }

    /**
     * debounce jakby miało wpaść w jakiś autystyczny loop
     * @type {DebouncedFunc<function(): undefined>}
     */
    fetchData = debounce((override = false) => {
        const {
            aggregated: {data},
            dispatch,
            device,
            start,
            end,
            isDuration,
            index,
            dataType,
            prefetchData,
            prefetchDays,
            tc,
            exactDatesOnly,
        } = this.props;
        if (!isFinite(start)) {
            DEBUG && console.log("[AggFetcher] start is not finite aborting");
            return;
        }
        DEBUG &&
            console.log(
                "[AggFetcher] checkIfHaveInAggregatedData",
                start,
                end,
                isDuration
            );
        let timestamp1 = start;
        let timestamp2 = isDuration
            ? end || +moment.utc().startOf("day")
            : null;
        /**
         * prefetchowanie danych, prefetchDays {number} ilość dni do preferchowania
         * jesli nastepny lub poprzedni dzien nie ma danych to pobieramy wieksza ilość danych aby użytkownik nie wołal za często api oraz nie miał ekranu ładowania
         */
        if (isNil(timestamp2) && prefetchData) {
            DEBUG &&
                console.log(
                    "[AggFetcher] using prefetchData with %s days",
                    prefetchDays
                );
            const tmp2 = +moment.utc(timestamp1).add(1, "days");
            const tmp1 = +moment.utc(timestamp1).subtract(1, "days");
            if (!tc.alreadyFetched(data, tmp1, tmp2)) {
                timestamp2 = +moment.utc(timestamp1).add(prefetchDays, "days");
                timestamp1 = +moment
                    .utc(timestamp1)
                    .subtract(prefetchDays, "days");
            }
        }
        if (override || !tc.alreadyFetched(data, timestamp1, timestamp2)) {
            if (isNil(timestamp2)) {
                DEBUG &&
                    console.log(
                        "[AggFetcher] getAggregatedData AggDataTime",
                        timestamp1
                    );
                dispatch(
                    getAggregatedData(device, {
                        AggDataTime: timestamp1,
                        index,
                        dataType,
                    })
                );
            } else {
                if (exactDatesOnly) {
                    DEBUG &&
                        console.log(
                            "[AggFetcher] getAggregatedData AggDataTimeFrom AggDataTimeTo EXACT DATES ONLY",
                            timestamp1,
                            timestamp2
                        );
                    dispatch(
                        getAggregatedData(device, {
                            AggDataTimeFrom: +moment
                                .utc(timestamp1)
                                .subtract(prefetchDays, "days"),
                            AggDataTimeTo: +moment
                                .utc(timestamp1)
                                .add(prefetchDays, "days"),
                            index,
                            dataType,
                        })
                    );
                    dispatch(
                        getAggregatedData(device, {
                            AggDataTimeFrom: +moment
                                .utc(timestamp2)
                                .subtract(prefetchDays, "days"),
                            AggDataTimeTo: +moment
                                .utc(timestamp2)
                                .add(prefetchDays, "days"),
                            index,
                            dataType,
                        })
                    );
                } else {
                    DEBUG &&
                        console.log(
                            "[AggFetcher] getAggregatedData AggDataTimeFrom AggDataTimeTo",
                            timestamp1,
                            timestamp2
                        );
                    dispatch(
                        getAggregatedData(device, {
                            AggDataTimeFrom: timestamp1,
                            AggDataTimeTo: timestamp2,
                            index,
                            dataType,
                        })
                    );
                }
            }
            tc.markAsFetched(timestamp1, timestamp2, exactDatesOnly);
        }
    }, 25);

    refresh = () => {
        const {
            aggregated: {lastModTime},
        } = this.props;
        this.setState(
            {
                loading: true,
                clicked: true,
                clickedTime: Date.now(),
                prevModTime: lastModTime,
                showDetails: true,
            },
            () => {
                this.requestMQTTAggData();
                this.fetchData(true);
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    this.setState({
                        showDetails: false,
                    });
                }, 12 * 1000);
            }
        );
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            prevProps.currentDayRefreshTime !== this.props.currentDayRefreshTime
        ) {
            this.setupInterval();
        }
        if (prevProps.aggregated.fetching !== this.props.aggregated.fetching) {
            if (!this.props.aggregated.fetching && this.state.loading) {
                this.setState({
                    loading: false,
                });
            }
        }
    }

    render() {
        const {
            showNewestDate,
            start,
            end,
            isDuration,
            aggregated: {data, lastFetched, fetching, lastModTime},
            t,
        } = this.props;
        const {clicked, prevModTime, showDetails, loading, clickedTime} =
            this.state;
        if (showNewestDate) {
            const startDate = moment.utc(start).startOf("day");
            const endDate = moment.utc(end ? end : null).endOf("day");
            const isSameDay =
                moment().format(moment.HTML5_FMT.DATE) ===
                startDate.format(moment.HTML5_FMT.DATE);
            const isLastDayOfDuration = isDuration
                ? moment(
                      moment.utc().format(moment.HTML5_FMT.DATE)
                  ).isSameOrBefore(
                      moment(endDate.format(moment.HTML5_FMT.DATE))
                  )
                : false;
            const shouldShowRefresh = isSameDay || isLastDayOfDuration;
            const day = maxBy(
                data.filter((o) =>
                    isDuration ? o.AggTi <= +endDate : o.AggTi === +startDate
                ),
                (o) => o.AggTi
            );
            const showLoading = clicked && loading;
            return (
                <div
                    className={
                        showDetails
                            ? "fetura-data-fetcher focused"
                            : "fetura-data-fetcher"
                    }>
                    <div className={"time"}>
                        {!!day &&
                            t("charts.dataLoadedForDate", {
                                date: shouldShowRefresh
                                    ? moment(
                                          day.DaMoTi ?? day.DtaModTime
                                      ).format("HH:mm DD.MM.YY")
                                    : moment.utc(day.AggTi).format("DD.MM.YY"),
                            })}
                        {shouldShowRefresh &&
                            !day &&
                            !fetching &&
                            t("aggFetcher.noData")}
                    </div>
                    <div className={"description"}>
                        {!showLoading && !!clicked && lastFetched !== 0 && (
                            <>
                                {prevModTime === lastModTime ? (
                                    t("aggFetcher.noNewerData")
                                ) : (
                                    <>
                                        {t("aggFetcher.refreshedAt")}{" "}
                                        <AgoTime date={clickedTime} />{" "}
                                    </>
                                )}
                            </>
                        )}
                        &nbsp;
                    </div>
                    {shouldShowRefresh && (
                        <>
                            <Button
                                title={
                                    clickedTime
                                        ? t("aggFetcher.lastRefreshAt", {
                                              value: localDateTimeFormatter(
                                                  clickedTime
                                              ),
                                          })
                                        : t("aggFetcher.refresh")
                                }
                                className={"refresh"}
                                buttonStyle={"bordered"}
                                isLoading={showLoading}
                                loadingText={
                                    <i
                                        className={
                                            "fa-fw me-1 fas fa-sync-alt fa-spin"
                                        }
                                    />
                                }
                                icon={
                                    <i
                                        className={"fa-fw me-1 fas fa-sync-alt"}
                                    />
                                }
                                allCaps={false}
                                text={t("aggFetcher.refresh")}
                                onClick={this.refresh}
                            />
                        </>
                    )}
                </div>
            );
        }
        return null;
    }
}

Fetcher.propTypes = {
    device: PropTypes.object.isRequired,
    index: PropTypes.number,
    // key: PropTypes.string.isRequired, // `${device.DevID}_${index}`
    start: PropTypes.number.isRequired, //utc timestamp
    end: PropTypes.number, //utc timestamp
    isDuration: PropTypes.bool,
    currentDayRefreshTime: PropTypes.number,
    showNewestDate: PropTypes.bool,
    prefetchData: PropTypes.bool,
    prefetchDays: PropTypes.number,
};

Fetcher.defaultProps = {
    currentDayRefreshTime: 0, // defaultowo wywalone,
    showNewestDate: false,
    prefetchData: true,
    prefetchDays: 5,
};

export default Fetcher;
